import {
  AnimationMixer,
  Box3,
  Camera,
  Group,
  LoopRepeat,
  Material,
  MathUtils,
  Mesh,
  MeshStandardMaterial,
  Object3D,
  Vector3,
} from "three";
import { GLBModel } from "./GLBModel";
import type { AmbientFishConfig, SceneProps } from "./SceneProps";
import {
  getAmbientFishCountForPollution,
  getAmbientFishMaxCount,
  getAmbientFishMaxShoalCount,
  getAmbientFishShoalCountForPollution,
  getAmbientFishShoalFishCounts,
} from "./AmbientFishShoals";

type AmbientFish = {
  group: Group;
  object: Object3D;
  mixer: AnimationMixer | null;
  orbit: FishOrbit;
  shoalIndex: number;
  bobPhase: number;
  offset: Vector3;
};

type FishOrbit = {
  centerX: number;
  centerZ: number;
  radiusX: number;
  radiusZ: number;
  height: number;
  speed: number;
  direction: 1 | -1;
  phase: number;
};

const tmpBox = new Box3();
const tmpSize = new Vector3();
const tmpCameraWorld = new Vector3();
const tmpCameraLocal = new Vector3();
const DEFAULT_MIXER_DELTA_SECONDS = 1 / 60;
const MAX_MIXER_DELTA_SECONDS = 0.05;
// Минимальная горизонтальная дистанция рыбы от камеры. Орбиты рыб проходят
// рядом с началом сцены, где обычно стоит пользователь, поэтому без зазора
// рыбы влетают в камеру и клипаются о near-plane (правка заказчика #5).
const MIN_FISH_CAMERA_DISTANCE = 1.45;

function getCameraLocalPosition(scene: Object3D, camera: Camera) {
  camera.getWorldPosition(tmpCameraWorld);
  tmpCameraLocal.copy(tmpCameraWorld);
  if (scene instanceof Group) {
    scene.worldToLocal(tmpCameraLocal);
  }
  return tmpCameraLocal;
}

function fitObjectToTargetSize(object: Object3D, targetSize: number) {
  object.updateMatrixWorld(true);
  tmpBox.setFromObject(object);
  tmpBox.getSize(tmpSize);

  const maxDimension = Math.max(tmpSize.x, tmpSize.y, tmpSize.z);
  if (maxDimension <= 0) return;

  const scale = targetSize / maxDimension;
  object.scale.multiplyScalar(scale);
}

function createAnimationMixer(model: unknown, object: Object3D) {
  if (!(model instanceof GLBModel)) return null;

  const clips = model.gltf?.animations ?? [];
  if (clips.length === 0) return null;

  const mixer = new AnimationMixer(object);
  clips.forEach((clip) => {
    const action = mixer.clipAction(clip);
    action.setLoop(LoopRepeat, Infinity);
    action.play();
  });

  return mixer;
}

function emptyLayer() {
  return {
    update: () => {},
    dispose: () => {},
  };
}

function createSingleAmbientFishLayer(
  props: Pick<SceneProps, "models" | "scene" | "camera"> & {
    ambientFish: AmbientFishConfig;
  },
  options: {
    roomSize: number;
    baseCameraHeight: number;
  },
) {
  const config = props.ambientFish;
  const fish: AmbientFish[] = [];

  if (!config?.visible || config.models.length === 0) {
    return emptyLayer();
  }

  const loadedModels = config.models
    .map((fishModel) => ({
      ...fishModel,
      loadedModel: props.models[fishModel.model.key],
    }))
    .filter((fishModel) => fishModel.loadedModel);

  if (loadedModels.length === 0) return emptyLayer();

  const shoalConfig = config.shoals;
  const shoalFishCounts = shoalConfig
    ? getAmbientFishShoalFishCounts(config)
    : [];
  const fishCount = shoalConfig
    ? shoalFishCounts.reduce((sum, count) => sum + count, 0)
    : getAmbientFishMaxCount(config);

  if (fishCount <= 0) return emptyLayer();

  const orbits: FishOrbit[] = [];

  const createOrbit = (
    index: number,
    phase = Math.random() * Math.PI * 2,
  ): FishOrbit => {
    const direction: 1 | -1 = index % 2 === 0 ? 1 : -1;
    const heightBands = [
      [options.baseCameraHeight * 0.78, options.baseCameraHeight * 1.05],
      [options.baseCameraHeight * 0.5, options.baseCameraHeight * 0.76],
      [options.baseCameraHeight * 0.92, options.baseCameraHeight * 1.22],
    ] as const;
    const heightBand = heightBands[index % heightBands.length];

    return {
      centerX: MathUtils.lerp(-0.18, 0.18, Math.random()),
      centerZ: MathUtils.lerp(-0.85, -1.05, Math.random()),
      radiusX: MathUtils.lerp(
        options.roomSize * 0.34,
        options.roomSize * 0.45,
        Math.random(),
      ),
      radiusZ: MathUtils.lerp(
        options.roomSize * 0.24,
        options.roomSize * 0.36,
        Math.random(),
      ),
      height: MathUtils.lerp(heightBand[0], heightBand[1], Math.random()),
      speed: MathUtils.lerp(0.16, 0.28, Math.random()),
      direction,
      phase,
    };
  };

  if (shoalConfig) {
    const maxShoalCount = getAmbientFishMaxShoalCount(config);
    for (let i = 0; i < maxShoalCount; i++) {
      orbits.push(createOrbit(i));
    }
  }

  for (let i = 0; i < fishCount; i++) {
    const fishModel = loadedModels[i % loadedModels.length];
    const object = fishModel.loadedModel.getModel();

    if (!object) continue;

    object.traverse((child) => {
      if (child instanceof Mesh) {
        child.material = new MeshStandardMaterial({
          color: child.material.color,
          map: child.material.map,
          transparent: child.material.transparent,
          roughness: 0.82,
          metalness: 0,
        });
      }
    });

    object.name = `ambient-fish-${i}`;
    object.userData.type = "ambientFish";
    object.userData.collectable = false;
    fitObjectToTargetSize(object, fishModel.targetSize);
    object.rotation.set(
      fishModel.rotation.x,
      fishModel.rotation.y,
      fishModel.rotation.z,
    );

    const group = new Group();
    group.name = `ambient-fish-group-${i}`;
    group.userData.type = "ambientFish";
    group.add(object);

    let shoalIndex = i;
    if (shoalConfig) {
      let cursor = 0;
      for (let j = 0; j < shoalFishCounts.length; j++) {
        cursor += shoalFishCounts[j];
        if (i < cursor) {
          shoalIndex = j;
          break;
        }
      }
    }
    const orbit = shoalConfig ? orbits[shoalIndex] : createOrbit(i);
    const offset = shoalConfig
      ? new Vector3(
        (Math.random() - 0.5) * shoalConfig.spread.x,
        (Math.random() - 0.5) * shoalConfig.spread.y,
        (Math.random() - 0.5) * shoalConfig.spread.z,
      )
      : new Vector3();

    fish.push({
      group,
      object,
      mixer: createAnimationMixer(fishModel.loadedModel, object),
      orbit,
      shoalIndex,
      bobPhase: Math.random() * Math.PI * 2,
      offset,
    });

    props.scene.add(group);
  }

  let lastElapsedSeconds: number | null = null;

  return {
    update: (elapsedSeconds: number, revealProgress = 1, pollutionLevel = 0) => {
      const rawDeltaSeconds =
        lastElapsedSeconds === null
          ? DEFAULT_MIXER_DELTA_SECONDS
          : elapsedSeconds - lastElapsedSeconds;
      const mixerDeltaSeconds = MathUtils.clamp(
        rawDeltaSeconds,
        0,
        MAX_MIXER_DELTA_SECONDS,
      );
      lastElapsedSeconds = elapsedSeconds;

      const reveal = Math.max(0, Math.min(1, revealProgress));
      const visible = reveal > 0.02;
      const scale = MathUtils.lerp(0.82, 1, reveal);
      const activeShoalCount = shoalConfig
        ? getAmbientFishShoalCountForPollution(config, pollutionLevel)
        : Number.POSITIVE_INFINITY;
      const activeFishCount = shoalConfig
        ? Number.POSITIVE_INFINITY
        : getAmbientFishCountForPollution(config, pollutionLevel);

      const cameraLocal = getCameraLocalPosition(props.scene, props.camera);

      fish.forEach((item, index) => {
        const orbit = item.orbit;
        const angle =
          orbit.phase + elapsedSeconds * orbit.speed * orbit.direction;
        const bob = Math.sin(elapsedSeconds * 1.2 + item.bobPhase) * 0.08;
        const tangentX = -Math.sin(angle) * orbit.radiusX * orbit.direction;
        const tangentZ = Math.cos(angle) * orbit.radiusZ * orbit.direction;

        item.group.position.set(
          orbit.centerX + Math.cos(angle) * orbit.radiusX + item.offset.x,
          orbit.height + bob + item.offset.y,
          orbit.centerZ +
            Math.sin(angle) * orbit.radiusZ +
            item.offset.z +
            Math.sin(elapsedSeconds * 0.45 + index) * 0.08,
        );
        const dx = item.group.position.x - cameraLocal.x;
        const dz = item.group.position.z - cameraLocal.z;
        const horizontalDistance = Math.hypot(dx, dz);
        if (
          horizontalDistance < MIN_FISH_CAMERA_DISTANCE &&
          horizontalDistance > 1e-4
        ) {
          const push = MIN_FISH_CAMERA_DISTANCE / horizontalDistance;
          item.group.position.x = cameraLocal.x + dx * push;
          item.group.position.z = cameraLocal.z + dz * push;
        }

        item.group.rotation.set(
          0,
          Math.atan2(tangentX, tangentZ),
          Math.sin(elapsedSeconds + index) * 0.04,
        );
        item.group.visible =
          visible && item.shoalIndex < activeShoalCount && index < activeFishCount;
        item.group.scale.setScalar(scale);
        item.mixer?.update(mixerDeltaSeconds);
      });
    },
    dispose: () => {
      fish.forEach(({ group }) => {
        group.removeFromParent();
      });
      fish.length = 0;
    },
  };
}

export function createAmbientFishLayer(
  props: Pick<SceneProps, "models" | "scene" | "ambientFish" | "camera">,
  options: {
    roomSize: number;
    baseCameraHeight: number;
  },
) {
  const configs = props.ambientFish
    ? Array.isArray(props.ambientFish) ? props.ambientFish : [props.ambientFish]
    : [];

  if (configs.length === 0) return emptyLayer();

  const layers = configs.map((config) =>
    createSingleAmbientFishLayer({ ...props, ambientFish: config }, options),
  );

  return {
    update: (elapsedSeconds: number, revealProgress?: number, pollutionLevel?: number) => {
      layers.forEach((l) => l.update(elapsedSeconds, revealProgress, pollutionLevel));
    },
    dispose: () => {
      layers.forEach((l) => l.dispose());
    },
  };
}
